import React, { useState, useEffect } from 'react';

export const AgeGate = ({ children }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [showGate, setShowGate] = useState(true);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const verified = localStorage.getItem('age_verified');
    if (verified === 'true') {
      setIsVerified(true);
      setShowGate(false);
    }
  }, []);

  // Set page title when age gate is shown
  useEffect(() => {
    if (showGate && !isVerified) {
      document.title = 'Welcome - Salt & Stone';
    }
  }, [showGate, isVerified]);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!day || !month || !year) {
      setError('Please enter your complete date of birth');
      return;
    }

    const birthDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const age = calculateAge(birthDate);

    if (age >= 18) {
      if (rememberMe) {
        localStorage.setItem('age_verified', 'true');
      } else {
        sessionStorage.setItem('age_verified', 'true');
      }
      setIsVerified(true);
      setShowGate(false);
    } else {
      setError('You must be 18 or older to enter this site');
    }
  };

  // Generate options
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  if (isVerified) {
    return children;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-[#050505] flex items-center justify-center" data-testid="age-gate-page">
      {/* Background with bottle */}
      <div className="absolute inset-0 overflow-hidden bg-[#050505]">
        {/* Mobile/Tablet bottle - Bottom style, same concept as desktop */}
        <div className="lg:hidden absolute left-1/2 -translate-x-1/4 bottom-0 translate-y-[30%] w-[150%] md:w-[90%]">
          <img
            src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/4304vchk_76094A3A-970E-4D4E-B858-3DDCB7CF4B52.PNG"
            alt="Salt & Stone Gin"
            className="w-full h-auto object-contain opacity-50"
          />
        </div>
        {/* Desktop bottle - center-right, cut at bottom */}
        <div className="hidden lg:block absolute left-1/2 -translate-x-1/4 bottom-0 translate-y-[35%] w-[50%] xl:w-[45%]">
          <img
            src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/4304vchk_76094A3A-970E-4D4E-B858-3DDCB7CF4B52.PNG"
            alt="Salt & Stone Gin"
            className="w-full h-auto object-contain opacity-70"
          />
        </div>
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-[#050505]/50"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md px-8 py-12 text-center">
        {/* Logo/Brand */}
        <div className="mb-10">
          <span className="block text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-3">
            Cyprus Craft Gin
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#F2F0E9] tracking-tight">
            Salt & Stone
          </h1>
        </div>

        {/* Divider */}
        <div className="w-16 h-px bg-[#D4AF37] mx-auto mb-10"></div>

        {/* Age verification message */}
        <p className="text-[#F2F0E9]/70 text-sm mb-8 leading-relaxed">
          Please enter your date of birth to verify you are of legal drinking age.
        </p>

        {/* Date of Birth Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-3 gap-3">
            {/* Day */}
            <div>
              <label className="block text-[10px] tracking-[0.15em] text-[#D4AF37] uppercase mb-2 text-left">
                Day
              </label>
              <select
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="w-full bg-transparent border border-white/20 text-[#F2F0E9] px-3 py-3 text-sm focus:border-[#D4AF37] outline-none transition-colors appearance-none cursor-pointer"
                data-testid="age-day"
              >
                <option value="" className="bg-[#050505]">DD</option>
                {days.map((d) => (
                  <option key={d} value={d} className="bg-[#050505]">{d}</option>
                ))}
              </select>
            </div>

            {/* Month */}
            <div>
              <label className="block text-[10px] tracking-[0.15em] text-[#D4AF37] uppercase mb-2 text-left">
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full bg-transparent border border-white/20 text-[#F2F0E9] px-3 py-3 text-sm focus:border-[#D4AF37] outline-none transition-colors appearance-none cursor-pointer"
                data-testid="age-month"
              >
                <option value="" className="bg-[#050505]">MM</option>
                {months.map((m) => (
                  <option key={m.value} value={m.value} className="bg-[#050505]">{m.label}</option>
                ))}
              </select>
            </div>

            {/* Year */}
            <div>
              <label className="block text-[10px] tracking-[0.15em] text-[#D4AF37] uppercase mb-2 text-left">
                Year
              </label>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full bg-transparent border border-white/20 text-[#F2F0E9] px-3 py-3 text-sm focus:border-[#D4AF37] outline-none transition-colors appearance-none cursor-pointer"
                data-testid="age-year"
              >
                <option value="" className="bg-[#050505]">YYYY</option>
                {years.map((y) => (
                  <option key={y} value={y} className="bg-[#050505]">{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Remember me */}
          <div className="flex items-center justify-center gap-2">
            <input
              type="checkbox"
              id="remember"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 accent-[#D4AF37] cursor-pointer"
            />
            <label htmlFor="remember" className="text-[#F2F0E9]/60 text-sm cursor-pointer">
              Remember me
            </label>
          </div>

          {/* Error message */}
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          {/* Submit button */}
          <button
            type="submit"
            className="btn-primary w-full"
            data-testid="age-verify-submit"
          >
            Enter
          </button>
        </form>

        {/* Footer disclaimer */}
        <div className="mt-10 space-y-4">
          <p className="text-[#F2F0E9]/30 text-xs">
            By entering this site, you agree to our Terms of Use and Privacy Policy.
          </p>
          <p className="text-[#F2F0E9]/30 text-xs">
            Please drink responsibly.
          </p>
        </div>
      </div>
    </div>
  );
};
